import { generateStableActuatorNameFromUuid } from "../../utils/actuatorIds";
import { sanitizeMjcfName } from "../../utils/mjcfNames";
import { getGlobalActuatorMapper } from "./globalMapper";
import { resolveUiActuatorControlKey } from "./uiControlKeys";
import type { Actuator, BodyRecord, JointRecord, WorldBodyEntry } from "./types";

/** Matches {@link ActuatorControlsPanel} slider keys (speed vs position groups). */
const SPEED_ACTUATOR_KINDS = new Set(["motor", "velocity", "intvelocity"]);

/**
 * Map worker `sim.ctrl` snapshot (keys = compiled MJCF actuator names) into physics-store buckets.
 */
export function applyWorkerActuatorCtrlSnapshotToStores(
  workerCtrlsByCompiledName: Record<string, number>,
  sceneState: {
    actuators?: Record<string, Actuator>;
    joints?: JointRecord;
    bodies?: BodyRecord;
    worldBodies?: WorldBodyEntry[];
  }
): { positionTargets: Record<string, number>; motorControls: Record<string, number> } {
  const mapper = getGlobalActuatorMapper();
  const positionTargets: Record<string, number> = {};
  const motorControls: Record<string, number> = {};

  const lookupCtrl = (compiledKey: string): number | undefined => {
    const direct = workerCtrlsByCompiledName[compiledKey];
    if (typeof direct === "number" && Number.isFinite(direct)) return direct;
    const san = sanitizeMjcfName(compiledKey);
    const alt = workerCtrlsByCompiledName[san];
    if (typeof alt === "number" && Number.isFinite(alt)) return alt;
    return undefined;
  };

  const actuators = Object.values(sceneState.actuators || {}).filter(Boolean) as Actuator[];
  for (const actuator of actuators) {
    let val: number | undefined;
    try {
      const compiledKey = mapper.getCompiledXmlControlKey(actuator);
      val = lookupCtrl(compiledKey);
      if (val === undefined && actuator.uuid) {
        val = lookupCtrl(generateStableActuatorNameFromUuid(actuator.uuid));
      }
      if (val === undefined && actuator.name) {
        val = lookupCtrl(actuator.name) ?? lookupCtrl(sanitizeMjcfName(actuator.name));
      }
    } catch {
      continue;
    }
    if (val === undefined || !Number.isFinite(val)) continue;

    const uiKey = resolveUiActuatorControlKey(actuator, sceneState);
    const kind = String(actuator.kind || actuator.type || "").toLowerCase();
    if (SPEED_ACTUATOR_KINDS.has(kind)) {
      motorControls[uiKey] = val;
    } else {
      positionTargets[uiKey] = val;
    }
  }

  return { positionTargets, motorControls };
}
